import { formatHumanName, type MedplumClient, type QueryTypes, resolveId } from "@medplum/core";
import type { Address, Identifier, Organization, Practitioner } from "@medplum/fhirtypes";
import {
  FhirResource,
  type SearchOptions,
  type SubscriptionCallback,
} from "@vibrant/models/fhir/FhirResource";
import type { ResourceData, ResourceRef } from "@vibrant/models/fhir/fhir-resource-types";
import { findIdentifier } from "@vibrant/models/fhir/fhir-resource-utils";
import dayjs from "dayjs";
import { z } from "zod";
import { NotFoundError } from "@/app/api/errors";
import { addProviderToTeam } from "@/data/scheduling/add-provider-to-team";
import type { CalAdminClient, CalManagedUser } from "@/lib/cal/admin";
import {
  CAL_IDENTIFIER_SYSTEM_TEAM_MEMBERSHIPS,
  CAL_IDENTIFIER_SYSTEM_TEAMS,
  CAL_IDENTIFIER_SYSTEM_USERS,
  CAL_IDENTIFIER_SYSTEM_USERS_EMAIL,
  CAL_IDENTIFIER_SYSTEM_USERS_USERNAME,
} from "@/lib/cal/client";
import {
  type DosespotPractitionerRoleType,
  DosespotPractitionerRoleTypeSchema,
} from "@/lib/dosespot/practitioner-role-type";
import { isCalConflictError, isCalInvalidEmailError } from "@/lib/errors/cal";
import { normalizeMedplumBinaryId } from "@/lib/files/utils";
import { FULLSCRIPT_IDENTIFIER_SYSTEM_PRACTITIONERS } from "@/lib/fullscript/types";
import { createLogger } from "@/lib/logger";
import { MEDMO_IDENTIFIER_SYSTEM_PRESCRIBERS } from "@/lib/medmo/types";
import { pruneSystemValuePairs, toFhirExtensions, toHumanName } from "@/lib/medplum/utils";
import { getStandardMembershipAccess } from "@/lib/migration/access-policy";
import { getVibrantSuperOrgId, sleep } from "@/lib/utils";
import { PHOTON_IDENTIFIER_SYSTEM_PRESCRIBERS } from "../../lib/photon/types";
import { Practice } from "../practice/Practice";
import { getIdentifierPatchOperations, type IdentifierPatchRequest } from "../utils";
import {
  type CalCredentials,
  createCalCredentials,
  patchCalCredentials,
  searchOneCalCredentials,
} from "./CalCredentials";
import {
  applyProviderDosespotPractitionerRoleType,
  getProviderDosespotPractitionerRoleType,
  getProviderDosespotRegistrationStatus,
  getProviderDosespotRoleSyncRequired,
  ProviderDosespotExtension,
} from "./dosespot";
import { ProviderRole } from "./ProviderRole";
import { RoleDefinition, VibrantRoles } from "./RoleDefinition";

const logger = createLogger("Provider");

export interface ProviderData {
  name: string;
  email: string;
  phone?: string;
  fax?: string;
  specialization?: string;
  address?: Address;
  practiceRef: string;
  active?: boolean;
  npi?: string;
}

export interface ProviderQualificationEntry {
  state: string;
  licenseNumber: string;
  deaNumber?: string;
}

export interface CreateProviderRequest {
  name: string;
  email: string;
  npi?: string;
  phone?: string;
  fax?: string;
  specialization?: string;
  address?: Address;
  active?: boolean;
  licenseNumber?: string;
  licenseState?: string;
  deaNumber?: string;
  qualifications?: ProviderQualificationEntry[];
  profilePictureFileId?: string;
  photonPrescriberId?: string;
  birthDate?: string;
  dosespotPractitionerRoleType?: DosespotPractitionerRoleType;
  dosespotRoleSyncRequired?: boolean | null;
}

export interface UpdateProviderRequest {
  name?: string;
  phone?: string;
  fax?: string;
  specialization?: string;
  address?: Address;
  active?: boolean;
  npi?: string;
  licenseNumber?: string;
  licenseState?: string;
  deaNumber?: string;
  qualifications?: ProviderQualificationEntry[];
  /** `null` clears the extension, resetting the provider to the initials empty state. */
  profilePictureFileId?: string | null;
  photonPrescriberId?: string;
  birthDate?: string;
  dosespotPractitionerRoleType?: DosespotPractitionerRoleType | null;
  dosespotRoleSyncRequired?: boolean | null;
}

export interface ProviderPatchQuery {
  npi?: string;
  calUserId?: number | null;
  calUsername?: string | null;
  calManagedEmail?: string | null;
  calTeamId?: number | null;
  calTeamMembershipId?: number | null;
  medmoPrescriberId?: number;
  photonPrescriberId?: string;
  fullscriptPractitionerId?: string;
  /** `null` clears the link — a Hint practitioner the practice deleted must not stay referenced. */
  hintPractitionerId?: string | null;
  active?: boolean;
}

export interface ProviderSearchQuery {
  id?: string;
  name?: string;
  practiceRef: string;
  practice?: ResourceRef;
  includeInactive?: boolean;
}

interface FindProviderInPracticeByEmailQuery {
  practiceRef: string;
  email: string;
  withRelations?: boolean;
}

interface FindProviderInPracticeByNpiQuery {
  practiceRef: string;
  npi: string;
  withRelations?: boolean;
}

interface SendProviderInviteInput {
  medplumAdmin: MedplumClient;
  provider: Provider;
  projectId?: string;
  forceNewMembership?: boolean;
  practiceIdentifier?: Identifier;
}

interface SendProviderInviteResult {
  hasAccessPolicy: boolean;
}

interface ProviderInvitePayloadResult {
  invitePayload: Parameters<MedplumClient["invite"]>[1];
  hasAccessPolicy: boolean;
}

type ForceMembershipDedupeDecision =
  | "force-membership-no-existing-user"
  | "force-membership-create-link"
  | "skip-force-membership-existing-link";

interface ForceMembershipDeduplicationResult {
  shouldForceNewMembership: boolean;
  decision: ForceMembershipDedupeDecision;
  userReference?: string;
}

interface ProviderInviteOptions {
  createNewProfile?: boolean;
  forceNewMembership?: boolean;
  practiceIdentifier?: Identifier;
  practiceOrganization?: string;
}

export type SerializedProvider = Practitioner;

interface ProviderSearchOptions extends SearchOptions {
  /** Whether to include providerRole and role relations. */
  withRelations?: boolean;
}

function normalizeNpi(value: string | null | undefined): string | undefined {
  const digits = value?.replace(/\D/g, "") ?? "";
  return digits || undefined;
}

export class Provider extends FhirResource<Practitioner> {
  static readonly ResourceType = "Practitioner";

  /**
   * Provider extensions.
   */
  static readonly Extension = {
    LicenseState: "vibrant.providers.license-state",
    ProfilePictureFileId: "vibrant.providers.profile-picture-file-id",
    HasCompletedOnboarding: "vibrant.providers.has-completed-onboarding",
    DosespotPractitionerRoleType: ProviderDosespotExtension.PractitionerRoleType,
    DosespotRoleSyncRequired: ProviderDosespotExtension.RoleSyncRequired,
    /** Written by the Medplum dosespot-enroll-prescriber-bot after each enrollment step. */
    DosespotRegistrationStatus: ProviderDosespotExtension.RegistrationStatus,
  } as const;

  /**
   * Qualification systems for multi-state license storage in Practitioner.qualification[].
   */
  static readonly Qualification = {
    StateLicense: "vibrant.providers.state-license",
    DeaLicense: "vibrant.providers.dea-license",
    LicenseStateSystem: "vibrant.providers.license-state",
  } as const;

  /**
   * Provider identifier systems.
   */
  static readonly Identifier = {
    LicenseNumber: "vibrant.providers.license-number",
    DEA: "vibrant.providers.dea-number",
    OldNPI: "vibrant.providers.npi",
    NPI: "http://hl7.org/fhir/sid/us-npi",
    /** Hint practitioner (`pct-…`) this provider maps to. Mirrors `Member.Identifier.HintPatientId`. */
    HintPractitionerId: "hint.practitioners",
    /** HL7 v2-0203 identifier type codes for typed identifiers. */
    Type: {
      System: "http://terminology.hl7.org/CodeSystem/v2-0203",
      DEALicense: "DEA",
      StateLicense: "SL",
    },
  } as const;

  /**
   * Provider data schema.
   */
  static readonly Schema = {
    LicenseState: z
      .string()
      .trim()
      .regex(/^[A-Z]{2}$/, "License state is required"),
    LicenseNumber: z.string().trim().min(1, "License number is required"),
    Dea: z
      .string()
      .trim()
      .regex(
        /^$|^[A-Z][A-Z9][0-9]{7}(-[A-Z0-9]{1,7})?$/,
        "DEA must match the DoseSpot registration format (e.g., BD1234567)",
      ),
    Npi: z.string().regex(/^\d{10}$/, "NPI must be exactly 10 digits"),
    /** Phone in E.164 format (+1 followed by 10 digits) */
    Phone: z
      .string()
      .trim()
      .regex(/^\+1\d{10}$/, "Phone must be a valid US phone number (e.g. +13125551234)"),
    /** Fax in E.164 format (+1 followed by 10 digits) */
    Fax: z
      .string()
      .trim()
      .regex(/^\+1\d{10}$/, "Fax must be a valid US fax number (e.g. +13125551234)"),
    /** Optional birth date - allows empty string or valid date with age 18-100 */
    BirthDate: z
      .string()
      .trim()
      .refine(
        (value) => {
          // Must have the format YYYY-MM-DD.
          if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

          // Must be a valid date.
          const date = dayjs(value, "YYYY-MM-DD", true);
          if (!date.isValid()) return false;

          // Must be between 18 and 100 years old.
          const age = dayjs().diff(date, "year");
          return age >= 18 && age <= 100;
        },
        {
          message:
            "Birth date must be valid (YYYY-MM-DD) and provider must be between 18 and 100 years old",
        },
      ),
    DosespotPractitionerRoleType: DosespotPractitionerRoleTypeSchema,
  };

  // Instance props.
  public role: RoleDefinition | undefined;
  public providerRole: ProviderRole | undefined;

  //------------------------------------------------------------------------------------------------
  //                                        GETTERS
  //------------------------------------------------------------------------------------------------
  get active(): boolean {
    return this.resource.active ?? false;
  }

  get email(): string | undefined {
    const email =
      this.resource.telecom?.find((telecom) => telecom.system === "email" && telecom.use !== "old")
        ?.value ?? this.resource.telecom?.find((telecom) => telecom.system === "email")?.value;
    return email;
  }

  get name(): string | undefined {
    const humanName = this.resource.name?.[0];
    return humanName ? formatHumanName(humanName) : undefined;
  }

  /**
   * Used when creating a Reference object to the provider.
   */
  get display(): string | undefined {
    return this.name;
  }

  get firstName(): string | undefined {
    const humanName = this.resource.name?.[0];
    return humanName?.given?.join(" ");
  }

  get lastName(): string | undefined {
    const humanName = this.resource.name?.[0];
    return humanName?.family;
  }

  get address(): Address | undefined {
    return this.resource.address?.[0];
  }

  get birthDate(): string | undefined {
    return this.resource.birthDate;
  }

  get phone(): string | undefined {
    return this.resource.telecom?.find((com) => com.system === "phone")?.value;
  }

  get telnyxFax(): string | undefined {
    return findIdentifier(this.resource, "fax")?.value;
  }

  get fax(): string | undefined {
    return this.resource.telecom?.find((com) => com.system === "fax")?.value;
  }

  get npi(): string | null {
    const npi = this.getIdentifier(Provider.Identifier.NPI);
    return npi?.toString() ?? null;
  }

  /** @deprecated Use stateLicenses instead. Reads from first qualification entry, falls back to legacy extension. */
  get licenseState(): string | undefined {
    return (
      this.stateLicenses[0]?.state ??
      this.getExtension(Provider.Extension.LicenseState)?.valueString
    );
  }

  /** @deprecated Use stateLicenses instead. Reads from first qualification entry, falls back to legacy identifier. */
  get licenseNumber(): string | undefined {
    return (
      this.stateLicenses[0]?.licenseNumber ??
      findIdentifier(this.resource, Provider.Identifier.LicenseNumber)?.value
    );
  }

  /** @deprecated Use deaLicenses instead. Reads from first DEA qualification entry, falls back to legacy identifier. */
  get deaNumber(): string | undefined {
    return (
      this.deaLicenses[0]?.deaNumber ??
      findIdentifier(this.resource, Provider.Identifier.DEA)?.value
    );
  }

  //------------------------------------------------------------------------------------------------
  //                                  MULTI-STATE QUALIFICATIONS
  //------------------------------------------------------------------------------------------------

  /** All state licenses extracted from Practitioner.qualification[]. */
  get stateLicenses(): { state: string; licenseNumber: string }[] {
    return this.getQualificationsBySystem(Provider.Qualification.StateLicense).map((q) => ({
      state: q.state,
      licenseNumber: q.value,
    }));
  }

  /** All DEA registrations extracted from Practitioner.qualification[]. */
  get deaLicenses(): { state: string; deaNumber: string }[] {
    return this.getQualificationsBySystem(Provider.Qualification.DeaLicense).map((q) => ({
      state: q.state,
      deaNumber: q.value,
    }));
  }

  /** Unique states where the provider holds at least one license or DEA. */
  get licensedStates(): string[] {
    const states = new Set<string>();
    for (const q of this.resource.qualification ?? []) {
      const hasVibrantCoding = q.code?.coding?.some(
        (c) =>
          c.system === Provider.Qualification.StateLicense ||
          c.system === Provider.Qualification.DeaLicense,
      );
      if (!hasVibrantCoding) continue;
      const state =
        q.issuer?.identifier?.system === Provider.Qualification.LicenseStateSystem
          ? q.issuer.identifier.value
          : undefined;
      if (state) states.add(state);
    }
    return [...states];
  }

  /** Combines license + DEA for a specific state, or undefined if not licensed there. */
  getLicenseForState(state: string): { licenseNumber?: string; deaNumber?: string } | undefined {
    const license = this.stateLicenses.find((l) => l.state === state);
    const dea = this.deaLicenses.find((d) => d.state === state);
    if (!license && !dea) return undefined;
    return {
      licenseNumber: license?.licenseNumber,
      deaNumber: dea?.deaNumber,
    };
  }

  /** Extracts qualification entries matching a given code system. */
  private getQualificationsBySystem(system: string): { state: string; value: string }[] {
    const results: { state: string; value: string }[] = [];
    for (const q of this.resource.qualification ?? []) {
      const coding = q.code?.coding?.find((c) => c.system === system);
      if (!coding?.code) continue;

      const state =
        q.issuer?.identifier?.system === Provider.Qualification.LicenseStateSystem
          ? q.issuer.identifier.value
          : undefined;
      if (!state) continue;

      results.push({ state, value: coding.code });
    }
    return results;
  }

  get profilePictureFileId(): string | undefined {
    return this.getExtension(Provider.Extension.ProfilePictureFileId)?.valueString;
  }

  /**
   * Binary id for avatar: Vibrant extension first, else first `Practitioner.photo` attachment
   * (some environments set FHIR photo without syncing the extension).
   */
  get resolvedProfilePictureBinaryId(): string | undefined {
    const pictureExts =
      this.resource.extension?.filter((e) => e.url === Provider.Extension.ProfilePictureFileId) ??
      [];

    for (const pictureExt of pictureExts) {
      const fromReference = normalizeMedplumBinaryId(pictureExt?.valueReference?.reference);
      if (fromReference) return fromReference;

      const fromIdentifier = normalizeMedplumBinaryId(pictureExt?.valueIdentifier?.value);
      if (fromIdentifier) return fromIdentifier;

      const fromExtensionString = normalizeMedplumBinaryId(pictureExt?.valueString);
      if (fromExtensionString) return fromExtensionString;
    }

    for (const photo of this.resource.photo ?? []) {
      const url = photo.url;
      if (!url) continue;

      const photoBinaryId = normalizeMedplumBinaryId(url);
      if (photoBinaryId) return photoBinaryId;
    }

    return undefined;
  }

  /** Current DoseSpot EPCS registration status (e.g. "Pending", "IDPInitializeSuccess", "IDPSuccess", "TFAActivateInit", "TFAActivatedSuccess"). */
  get dosespotRegistrationStatus(): string | undefined {
    return getProviderDosespotRegistrationStatus(this.resource);
  }

  /** Persisted Vibrant-side role used to build DoseSpot practitionerRoleTypes. */
  get dosespotPractitionerRoleType(): string | undefined {
    return getProviderDosespotPractitionerRoleType(this.resource);
  }

  /** True when the next DoseSpot entry should update the vendor-side clinician role. */
  get dosespotRoleSyncRequired(): boolean {
    return getProviderDosespotRoleSyncRequired(this.resource);
  }

  get calUserId(): number | null {
    const identifier = findIdentifier(this.resource, CAL_IDENTIFIER_SYSTEM_USERS);
    if (!identifier) {
      return null;
    }

    const parsed = parseInt(identifier.value ?? "", 10);
    if (Number.isNaN(parsed)) {
      throw new Error(`Invalid Cal.com user ID: ${identifier.value}`);
    }
    return parsed;
  }

  get calUsername(): string | null {
    const identifier = findIdentifier(this.resource, CAL_IDENTIFIER_SYSTEM_USERS_USERNAME);
    return identifier?.value ?? null;
  }

  /**
   * The Cal.com managed email for this provider.
   */
  get calManagedEmail(): string | null {
    const identifier = findIdentifier(this.resource, CAL_IDENTIFIER_SYSTEM_USERS_EMAIL);
    return identifier?.value ?? null;
  }

  get calTeamId(): number | null {
    const identifier = findIdentifier(this.resource, CAL_IDENTIFIER_SYSTEM_TEAMS);
    if (!identifier) {
      return null;
    }

    const parsed = parseInt(identifier.value ?? "", 10);
    if (Number.isNaN(parsed)) {
      throw new Error(`Invalid Cal.com team ID: ${identifier.value}`);
    }
    return parsed;
  }

  get calTeamMembershipId(): number | null {
    const identifier = findIdentifier(this.resource, CAL_IDENTIFIER_SYSTEM_TEAM_MEMBERSHIPS);
    if (!identifier) {
      return null;
    }

    const parsed = parseInt(identifier.value ?? "", 10);
    if (Number.isNaN(parsed)) {
      throw new Error(`Invalid Cal.com team membership ID: ${identifier.value}`);
    }
    return parsed;
  }

  get medmoPrescriberId(): number | null {
    const identifier = findIdentifier(this.resource, MEDMO_IDENTIFIER_SYSTEM_PRESCRIBERS);
    if (!identifier) {
      return null;
    }
    const parsed = parseInt(identifier.value ?? "", 10);
    if (Number.isNaN(parsed)) {
      throw new Error(`Invalid Medmo Prescriber ID: ${identifier.value}`);
    }
    return parsed;
  }

  get fullscriptPractitionerId(): string | null {
    const identifier = findIdentifier(this.resource, FULLSCRIPT_IDENTIFIER_SYSTEM_PRACTITIONERS);
    return identifier?.value ?? null;
  }

  get hintPractitionerId(): string | null {
    const identifier = findIdentifier(this.resource, Provider.Identifier.HintPractitionerId);
    return identifier?.value ?? null;
  }

  get hasCompletedOnboarding(): boolean {
    // Admins and contacts do not need clinician onboarding.
    if (this.role?.roleId === VibrantRoles.Admin || this.role?.roleId === VibrantRoles.Contact) {
      return true;
    }

    if (!this.role) {
      // role is undefined — admin shortcut was skipped not because the provider isn't an admin,
      // but because ProviderRole wasn't loaded. This may produce a false negative.
      logger.warn("hasCompletedOnboarding evaluated without a role — returning extension value", {
        providerId: this.id,
      });
    }

    return this.getExtension(Provider.Extension.HasCompletedOnboarding)?.valueBoolean ?? false;
  }

  //------------------------------------------------------------------------------------------------
  //                                     INSTANCE METHODS
  //------------------------------------------------------------------------------------------------

  /**
   * Marks the provider as having completed onboarding.
   *
   * @param medplum - The Medplum client.
   * @returns The updated provider.
   */
  async markOnboardingComplete(medplum: MedplumClient): Promise<Provider> {
    const updated = await Provider.patchResource(medplum, {
      id: this.id,
      patch: {
        extension: toFhirExtensions([
          { url: Provider.Extension.HasCompletedOnboarding, valueBoolean: true },
        ]),
      },
    });
    return new Provider(updated);
  }

  /**
   * Resets the provider's onboarding status to incomplete.
   *
   * @param medplum - The Medplum client.
   * @returns The updated provider.
   */
  async resetOnboardingStatus(medplum: MedplumClient): Promise<Provider> {
    const updated = await Provider.patchResource(medplum, {
      id: this.id,
      patch: {
        extension: toFhirExtensions([
          { url: Provider.Extension.HasCompletedOnboarding, valueBoolean: false },
        ]),
      },
    });
    return new Provider(updated);
  }

  async isVibrantAdmin(medplum: MedplumClient): Promise<boolean> {
    const superOrgId = getVibrantSuperOrgId();
    const providerRole = await ProviderRole.searchOne(medplum, {
      practitioner: this.referenceObject,
      organization: { reference: `Organization/${superOrgId}` },
    });

    return Boolean(providerRole);
  }

  /**
   * Gets the practice that the provider belongs to.
   *
   * @param medplum - The Medplum client.
   * @returns The practice.
   */
  async getPractice(medplum: MedplumClient): Promise<Practice | undefined> {
    // Try to get the provider role from the instance.
    let providerRole: ProviderRole | undefined = this.providerRole;

    // If the provider role is not set in the instance, fetch it from medplum.
    if (!providerRole) {
      providerRole = await ProviderRole.searchOne(medplum, {
        practitioner: this.referenceObject,
      });
    }

    if (!providerRole) return undefined;

    const practiceId = resolveId(providerRole.organization);
    if (!practiceId) return undefined;

    return await Practice.getById(medplum, practiceId);
  }

  /**
   * @deprecated Use {@link getPractice} instead.
   */
  async getAllPractices(medplum: MedplumClient): Promise<Practice[]> {
    const superOrgId = getVibrantSuperOrgId();
    let organizations: Organization[];
    const isVibrantAdmin = await this.isVibrantAdmin(medplum);

    if (isVibrantAdmin) {
      // return all sub-organizations
      organizations = await medplum.searchResources("Organization", {
        partof: `Organization/${superOrgId}`,
        _count: "1000",
      });
    } else {
      const practitionerRoles = await ProviderRole.search(medplum, {
        practitioner: this.referenceObject,
      });

      const organizationIds = practitionerRoles
        .map((role) => role.organization)
        .filter((organization) => organization !== undefined)
        .map((organization) => resolveId(organization))
        .filter((id) => id !== undefined);

      organizations = await medplum.searchResources("Organization", {
        _id: organizationIds.join(","),
        partof: `Organization/${superOrgId}`,
      });
    }
    return organizations.map((organization) => new Practice(organization));
  }

  async getCalCredentials(medplum: MedplumClient): Promise<CalCredentials | undefined> {
    return searchOneCalCredentials(medplum, {
      providerId: this.id,
    });
  }

  /**
   * Resolves the managed Cal.com user for a provider.
   * Uses the stored managed email and direct Cal user ID instead of broad list scans.
   */
  static async getManagedUserForProvider(
    calAdmin: CalAdminClient,
    provider: Provider,
  ): Promise<CalManagedUser["user"] | null> {
    const emailCandidates = Array.from(
      new Set(
        [provider.email, provider.calManagedEmail].filter((email): email is string =>
          Boolean(email?.trim()),
        ),
      ),
    );

    for (const email of emailCandidates) {
      try {
        const byEmail = await calAdmin.findManagedUserByEmail(email);
        if (byEmail) {
          return byEmail;
        }
      } catch (error) {
        const isInvalidEmailLookup = isCalInvalidEmailError(error);
        if (!isInvalidEmailLookup) {
          throw error;
        }

        logger.warn("Skipping invalid provider managed-user email during Cal lookup", {
          providerId: provider.id,
          email,
        });
      }
    }

    let providerCalUserId: number | null = null;
    try {
      providerCalUserId = provider.calUserId;
    } catch (error) {
      logger.warn("Provider has malformed Cal user ID while resolving managed Cal user", {
        providerId: provider.id,
        error,
      });
      return null;
    }

    if (providerCalUserId == null) {
      return null;
    }

    try {
      return await calAdmin.getManagedUser(providerCalUserId);
    } catch (error) {
      logger.warn("Unable to resolve provider managed Cal user by ID", {
        providerId: provider.id,
        calUserId: providerCalUserId,
        emailCandidates,
        error,
      });
      return null;
    }
  }

  async clearCalUser(medplum: MedplumClient): Promise<Provider> {
    const updated = await Provider.patchResource(medplum, {
      id: this.id,
      patch: {
        identifier: [
          {
            system: CAL_IDENTIFIER_SYSTEM_USERS,
            value: null,
          },
          {
            system: CAL_IDENTIFIER_SYSTEM_USERS_USERNAME,
            value: null,
          },
          {
            system: CAL_IDENTIFIER_SYSTEM_USERS_EMAIL,
            value: null,
          },
          {
            system: CAL_IDENTIFIER_SYSTEM_TEAMS,
            value: null,
          },
          {
            system: CAL_IDENTIFIER_SYSTEM_TEAM_MEMBERSHIPS,
            value: null,
          },
        ],
      },
    });
    return new Provider(updated);
  }

  //------------------------------------------------------------------------------------------------
  //                                       CLASS METHODS
  //------------------------------------------------------------------------------------------------

  /**
   * Converts a provider query to a FHIR search query.
   *
   * @param query - The provider query.
   * @returns The FHIR search query.
   */
  static toFhirQuery(query: ProviderSearchQuery) {
    return {
      name: query.name,
      organization: { reference: query.practiceRef },
    };
  }

  /**
   * Creates a provider from the current user profile.
   *
   * @param medplum - The Medplum client.
   * @returns The provider.
   */
  static async fromProfile(medplum: MedplumClient): Promise<Provider> {
    const profile = await medplum.getProfileAsync();

    if (profile?.resourceType !== "Practitioner") {
      throw new Error("Unsupported profile type");
    }

    if (!profile.id) {
      throw new Error("Provider profile does not have an ID");
    }

    return await Provider.getById(medplum, profile.id);
  }

  /**
   * Fetches a single provider by ID.
   *
   * @param medplum - The Medplum client.
   * @param providerId - The ID of the provider.
   * @returns The provider.
   */
  static async getById(
    medplum: MedplumClient,
    providerId: string,
    withRelations = true,
  ): Promise<Provider> {
    const provider = await Provider.searchOne(medplum, { id: providerId }, withRelations);
    if (!provider) throw new NotFoundError("Provider not found");

    return provider;
  }

  /**
   * Resolves which of the given practitioner ids the caller can actually read, in one round trip.
   *
   * For validating a batch of references before publishing them: resolving each with `getById`
   * would issue one request per reference, and a miss would throw rather than simply drop out.
   * Ids the caller can't read (wrong compartment, deleted, never existed) are absent from the
   * result rather than raising, which is what makes this usable as a filter.
   *
   * Mirrors the batching in {@link Member.getManyById}.
   *
   * @param medplum - The Medplum client; its access policy decides what resolves.
   * @param providerIds - The practitioner ids to resolve; duplicates are collapsed.
   * @returns The subset of ids the caller can read.
   */
  static async resolveReadableIds(
    medplum: MedplumClient,
    providerIds: string[],
  ): Promise<Set<string>> {
    const uniqueIds = Array.from(new Set(providerIds.filter(Boolean)));
    const readableIds = new Set<string>();
    if (uniqueIds.length === 0) {
      return readableIds;
    }

    // Chunked because the ids ride in the query string, and a long enough list would exceed URL
    // limits. `_elements: id` because existence is the whole question - no other field is read.
    const chunkSize = 100;
    for (let index = 0; index < uniqueIds.length; index += chunkSize) {
      const idChunk = uniqueIds.slice(index, index + chunkSize);
      const resources = await medplum.searchResources("Practitioner", {
        _id: idChunk.join(","),
        _elements: "id",
        _count: idChunk.length,
      });

      for (const resource of resources) {
        if (resource.id) {
          readableIds.add(resource.id);
        }
      }
    }

    return readableIds;
  }

  /**
   * Fetches a single provider by identifier.
   *
   * @param medplum - The Medplum client.
   * @param identifier.system - The system of the identifier.
   * @param identifier.value - The value of the identifier.
   * @returns The provider.
   */
  static async getByIdentifier(
    medplum: MedplumClient,
    identifier: { system: string; value: string },
  ): Promise<Provider | undefined> {
    return (
      (await Provider.searchOne(medplum, {
        identifier: `${identifier.system}|${identifier.value}`,
      })) || undefined
    );
  }

  /**
   * Searches for providers by name.
   *
   * @param medplum - The Medplum client.
   * @param query - The search query.
   * @returns The providers.
   */
  static async search(
    medplum: MedplumClient,
    query: ProviderSearchQuery,
    options?: ProviderSearchOptions,
  ): Promise<Provider[]> {
    const withRelations = options?.withRelations ?? false;
    logger.info(`Search Provider: ${JSON.stringify(query)}`);

    // Build the search query for PractitionerRole with _include to fetch Practitioners in one query
    const searchParams: QueryTypes = {
      organization: query.practiceRef,
      _include: "PractitionerRole:practitioner",
      _count: options?.limit ?? 1000,
      _offset: options?.offset ?? 0,
    };
    if (!query.includeInactive) {
      searchParams.active = true;
    }

    // Execute the search and get the full Bundle
    const bundle = await medplum.search("PractitionerRole", searchParams);

    // Extract PractitionerRole and Practitioner resources from the bundle
    const practitionerRolesById = new Map<string, ProviderRole>();
    const practitionersById = new Map<string, Practitioner>();
    const practitionerIdToRoleId = new Map<string, string>();

    for (const entry of bundle.entry ?? []) {
      const resource = entry.resource;
      if (!resource?.id) continue;

      if (resource.resourceType === "PractitionerRole") {
        const role = new ProviderRole(resource);
        practitionerRolesById.set(resource.id, role);

        // Map practitioner ID to role ID for later lookup
        const practitionerId = resolveId(role.practitioner);
        if (practitionerId) {
          practitionerIdToRoleId.set(practitionerId, resource.id);
        }
      } else if (resource.resourceType === "Practitioner") {
        // Cast through unknown since TypeScript doesn't narrow bundle entry types properly
        practitionersById.set(resource.id, resource as unknown as Practitioner);
      }
    }

    // Fetch role definitions if needed for relations
    const roleDefinitions = withRelations ? await RoleDefinition.search(medplum) : [];

    // Build Provider objects from Practitioners
    const providers: Provider[] = [];
    const seenProviderIds = new Set<string>();

    for (const [practitionerId, practitioner] of practitionersById) {
      // Skip duplicates (a provider might have multiple roles)
      if (seenProviderIds.has(practitionerId)) continue;
      seenProviderIds.add(practitionerId);

      const provider = new Provider(practitioner);

      // Skip providers that don't match the name filter
      if (query.name && !provider.name?.toLowerCase()?.includes(query.name.toLowerCase())) {
        continue;
      }

      if (withRelations) {
        // Find the provider's role for this practice
        const roleId = practitionerIdToRoleId.get(practitionerId);
        if (roleId) {
          const providerRole = practitionerRolesById.get(roleId);
          if (providerRole) {
            provider.providerRole = providerRole;
            provider.role = roleDefinitions.find((role) => role.roleId === providerRole.roleId);
          }
        }
      }

      providers.push(provider);
    }

    return providers;
  }

  /**
   * Searches for all providers matching the query, handling pagination internally.
   * Note: Limited to maxPages iterations to prevent excessive memory/CPU use.
   *
   * @param medplum - The Medplum client.
   * @param query - The search query.
   * @param withRelations - Whether to include providerRole and role relations.
   * @returns All matching providers.
   */
  static async searchAll(
    medplum: MedplumClient,
    query: ProviderSearchQuery,
    withRelations = false,
  ): Promise<Provider[]> {
    const pageSize = 100;
    const maxPages = 100; // Safeguard to prevent infinite loops on very large result sets
    const allProviderIds = new Set<string>();
    const allProviders: Provider[] = [];
    let offset = 0;
    let iterations = 0;

    while (true) {
      const providers = await Provider.search(medplum, query, {
        limit: pageSize,
        offset,
        withRelations,
      });

      // No more results - we've fetched everything
      if (providers.length === 0) {
        break;
      }

      // Add only providers we haven't seen yet (handles overlap from deduplication)
      for (const provider of providers) {
        if (!allProviderIds.has(provider.id)) {
          allProviderIds.add(provider.id);
          allProviders.push(provider);
        }
      }

      offset += pageSize;
      iterations++;

      // Safeguard: break if we've exceeded max pages to avoid excessive memory/CPU use
      if (iterations >= maxPages) {
        logger.warn(
          `searchAll: Reached maximum page limit (${maxPages}) with ${allProviders.length} results. ` +
            `Returning partial results. Query: ${JSON.stringify(query)}`,
        );
        break;
      }
    }

    return allProviders;
  }

  /**
   * Finds a provider by email within a single practice scope.
   *
   * @param medplum - The Medplum client.
   * @param query.practiceRef - Practice reference (e.g. Organization/{id}).
   * @param query.email - Provider email.
   * @param query.withRelations - Whether to include providerRole and role relations.
   * @returns Matching provider in practice, if found.
   */
  static async findInPracticeByEmail(
    medplum: MedplumClient,
    query: FindProviderInPracticeByEmailQuery,
  ): Promise<Provider | undefined> {
    const normalizedEmail = query.email.trim().toLowerCase();
    if (!normalizedEmail) {
      return undefined;
    }

    const providersInPractice = await Provider.searchAll(
      medplum,
      {
        practiceRef: query.practiceRef,
        includeInactive: true,
      },
      query.withRelations ?? false,
    );

    return providersInPractice.find(
      (provider) => provider.email?.trim().toLowerCase() === normalizedEmail,
    );
  }

  /**
   * Finds a provider by NPI within a single practice scope.
   *
   * @param medplum - The Medplum client.
   * @param query.practiceRef - Practice reference (e.g. Organization/{id}).
   * @param query.npi - Provider NPI.
   * @param query.withRelations - Whether to include providerRole and role relations.
   * @returns Matching provider in practice, if found.
   */
  static async findInPracticeByNpi(
    medplum: MedplumClient,
    query: FindProviderInPracticeByNpiQuery,
  ): Promise<Provider | undefined> {
    const normalizedNpi = normalizeNpi(query.npi);
    if (!normalizedNpi) {
      return undefined;
    }

    const providersInPractice = await Provider.searchAll(
      medplum,
      {
        practiceRef: query.practiceRef,
        includeInactive: true,
      },
      query.withRelations ?? false,
    );

    return providersInPractice.find((provider) => normalizeNpi(provider.npi) === normalizedNpi);
  }

  /**
   * Searches for a single provider by email, Cal.com user ID, Cal.com username, or other identifiers.
   *
   * Multiple identifier-based filters can be provided and will be combined as an OR-search.
   *
   * @param medplum - The Medplum client.
   * @param searchQuery - The search query.
   * @returns The provider.
   */
  static async searchOne(
    medplum: MedplumClient,
    searchQuery: {
      email?: string;
      calUserId?: number;
      calUsername?: string;
      id?: string;
      npi?: string;
      photonPrescriberId?: string;
      identifier?: string;
      fullscriptPractitionerId?: string;
    },
    withRelations = false,
  ): Promise<Provider | null> {
    // If no filters were provided, return null.
    if (Object.keys(searchQuery).length === 0) return null;

    const query: QueryTypes = {};

    if (searchQuery.email) {
      query.email = searchQuery.email;
    }

    if (searchQuery.id) {
      query._id = searchQuery.id;
    }

    // Build identifier filters as an array - FHIR supports comma-separated for OR-search
    const identifiers: string[] = [];

    if (searchQuery.calUserId !== undefined) {
      identifiers.push(`${CAL_IDENTIFIER_SYSTEM_USERS}|${searchQuery.calUserId}`);
    }

    if (searchQuery.calUsername) {
      identifiers.push(`${CAL_IDENTIFIER_SYSTEM_USERS_USERNAME}|${searchQuery.calUsername}`);
    }

    if (searchQuery.npi) {
      identifiers.push(`${Provider.Identifier.NPI}|${searchQuery.npi}`);
    }

    if (searchQuery.photonPrescriberId) {
      identifiers.push(`${PHOTON_IDENTIFIER_SYSTEM_PRESCRIBERS}|${searchQuery.photonPrescriberId}`);
    }

    if (searchQuery.fullscriptPractitionerId) {
      identifiers.push(
        `${FULLSCRIPT_IDENTIFIER_SYSTEM_PRACTITIONERS}|${searchQuery.fullscriptPractitionerId}`,
      );
    }

    if (searchQuery.identifier) {
      identifiers.push(searchQuery.identifier);
    }

    if (identifiers.length > 0) {
      query.identifier = identifiers.join(",");
    }

    const resource = await Provider.searchOneResource(medplum, query);
    if (!resource) return null;

    const provider = new Provider(resource);

    if (withRelations) {
      const providerRole = await ProviderRole.searchOne(medplum, {
        practitioner: provider.referenceObject,
      });

      if (providerRole) {
        provider.role = await providerRole.getRoleDefinition(medplum);
        provider.providerRole = providerRole;
      } else {
        // No PractitionerRole found — hasCompletedOnboarding will fall back to the extension value
        // and the admin shortcut will be skipped. This may cause the sidebar to collapse incorrectly.
        logger.warn("Provider loaded with no ProviderRole", { providerId: provider.id });
      }
    }

    return provider;
  }

  /**
   * Resolves the Medplum project ID required for provider invites.
   */
  private static getInviteProjectId(): string {
    const projectId = process.env.NEXT_PUBLIC_MEDPLUM_PROJECT_ID;
    if (!projectId) {
      throw new Error("NEXT_PUBLIC_MEDPLUM_PROJECT_ID is not set");
    }

    return projectId;
  }

  /**
   * Prevent duplicate (user, profile) memberships when forceNewMembership is requested on retries.
   */
  private static async dedupeForcedMembershipInvite({
    medplumAdmin,
    provider,
  }: {
    medplumAdmin: MedplumClient;
    provider: Provider;
  }): Promise<ForceMembershipDeduplicationResult> {
    if (!provider.email) {
      return {
        shouldForceNewMembership: true,
        decision: "force-membership-no-existing-user",
      };
    }

    const userReference = await Provider.findUserReferenceByEmail({
      medplumAdmin,
      email: provider.email,
    });

    if (!userReference) {
      return {
        shouldForceNewMembership: true,
        decision: "force-membership-no-existing-user",
      };
    }

    const existingMembership = await medplumAdmin.searchResources("ProjectMembership", {
      profile: provider.referenceString,
      user: userReference,
      _count: 1,
    });

    if (existingMembership.length > 0) {
      return {
        shouldForceNewMembership: false,
        decision: "skip-force-membership-existing-link",
        userReference,
      };
    }

    return {
      shouldForceNewMembership: true,
      decision: "force-membership-create-link",
      userReference,
    };
  }

  /**
   * Resolves an existing Medplum User reference by email.
   */
  private static async findUserReferenceByEmail({
    medplumAdmin,
    email,
  }: {
    medplumAdmin: MedplumClient;
    email: string;
  }): Promise<string | undefined> {
    const trimmedEmail = email.trim();
    const normalizedEmail = trimmedEmail.toLowerCase();
    const candidates = [...new Set([normalizedEmail, trimmedEmail])];

    for (const candidate of candidates) {
      const user = await medplumAdmin.searchOne("User", { email: candidate });
      if (user?.id) {
        return `User/${user.id}`;
      }

      const users = await medplumAdmin.searchResources("User", {
        email: candidate,
        _count: 5,
      });
      const userWithId = users.find(
        (candidateUser) => typeof candidateUser.id === "string" && candidateUser.id.length > 0,
      );
      if (userWithId?.id) {
        return `User/${userWithId.id}`;
      }
    }

    return undefined;
  }

  /**
   * Builds the invite payload for an existing provider.
   */
  private static async buildInvitePayload({
    medplumAdmin,
    provider,
    forceNewMembership = false,
    practiceIdentifier,
  }: {
    medplumAdmin: MedplumClient;
    provider: Provider;
    forceNewMembership?: boolean;
    practiceIdentifier?: Identifier;
  }): Promise<ProviderInvitePayloadResult> {
    if (!provider.id) {
      throw new Error("Provider ID is required to create an invite");
    }
    if (!provider.email) {
      throw new Error("Provider email is required to create an invite");
    }

    const firstName = provider.firstName ?? provider.lastName ?? provider.name ?? "";
    const lastName = provider.lastName ?? provider.firstName ?? provider.name ?? "";

    // Resolve the standard AccessPolicy for quarantine exclusion. Throws in
    // prod/sandbox if the policy is missing — refuse to invite an unprotected user.
    const membershipAccess = await getStandardMembershipAccess(medplumAdmin);
    const invitePayload: Parameters<MedplumClient["invite"]>[1] & {
      forceNewMembership?: boolean;
    } = {
      resourceType: "Practitioner",
      scope: "project",
      externalId: provider.id,
      firstName,
      lastName,
      email: provider.email,
      sendEmail: false,
      // upsert: true makes retry safe after partial failure (User created but ProjectMembership failed)
      upsert: true,
      // Explicitly bind membership to this specific Practitioner profile.
      // This avoids ambiguous profile lookup when multiple Practitioners share an email.
      membership: {
        profile: provider.referenceObject,
        // Assign AccessPolicy to exclude quarantined resources from provider view
        ...(membershipAccess && { access: membershipAccess }),
        ...(practiceIdentifier && { identifier: [practiceIdentifier] }),
      },
    };
    if (forceNewMembership) {
      invitePayload.forceNewMembership = true;
    }

    return {
      invitePayload,
      hasAccessPolicy: Boolean(membershipAccess),
    };
  }

  /**
   * Sends a Medplum invite for an existing provider.
   */
  static async sendInvite({
    medplumAdmin,
    provider,
    projectId,
    forceNewMembership = false,
    practiceIdentifier,
  }: SendProviderInviteInput): Promise<SendProviderInviteResult> {
    const resolvedProjectId = projectId ?? Provider.getInviteProjectId();
    const forceMembershipResult = forceNewMembership
      ? await Provider.dedupeForcedMembershipInvite({ medplumAdmin, provider })
      : undefined;
    const appliedForceNewMembership =
      forceMembershipResult?.shouldForceNewMembership ?? forceNewMembership;

    if (forceMembershipResult) {
      logger.info("Provider force membership dedupe decision", {
        providerId: provider.id,
        profileReference: provider.referenceString,
        requestedForceNewMembership: true,
        appliedForceNewMembership,
        decision: forceMembershipResult.decision,
        userReference: forceMembershipResult.userReference,
      });
    }

    const { invitePayload, hasAccessPolicy } = await Provider.buildInvitePayload({
      medplumAdmin,
      provider,
      forceNewMembership: appliedForceNewMembership,
      practiceIdentifier,
    });

    await medplumAdmin.invite(resolvedProjectId, invitePayload);

    return { hasAccessPolicy };
  }

  /**
   * Invites a provider to Vibrant. NOTE: this must be called by a Medplum admin.
   *
   * @param medplumAdmin - The Medplum client with admin privileges.
   * @param request - The request.
   * @returns The provider.
   */
  static async invite(
    medplumAdmin: MedplumClient,
    request: CreateProviderRequest,
    options: ProviderInviteOptions = {},
  ): Promise<Provider> {
    const projectId = Provider.getInviteProjectId();

    let provider = options.createNewProfile
      ? null
      : await Provider.searchOne(medplumAdmin, { email: request.email });

    if (provider) {
      provider = await Provider.update(medplumAdmin, provider.id, {
        name: request.name,
        npi: request.npi,
        phone: request.phone,
        fax: request.fax,
        specialization: request.specialization,
        address: request.address,
        qualifications: request.qualifications,
        profilePictureFileId: request.profilePictureFileId,
        dosespotPractitionerRoleType: request.dosespotPractitionerRoleType,
        active: request.active,
      });
    } else {
      const practitioner = await Provider.createResource(
        medplumAdmin,
        {
          ...Provider.toFhirObject(request),
          active: true,
        },
        { practiceOrganization: options.practiceOrganization },
      );
      provider = new Provider(practitioner);
    }

    await Provider.sendInvite({
      medplumAdmin,
      provider,
      projectId,
      forceNewMembership: options.forceNewMembership,
      practiceIdentifier: options.practiceIdentifier,
    });

    return provider;
  }

  /**
   * Converts a create provider request to a FHIR practitioner resource.
   *
   * @param data - The create provider request.
   * @returns The FHIR practitioner resource.
   */
  static toFhirObject(
    data: CreateProviderRequest | UpdateProviderRequest,
  ): ResourceData<Practitioner> {
    return {
      name: toHumanName(data.name),
      active: data.active,
      birthDate: data.birthDate,
      telecom: pruneSystemValuePairs([
        { system: "email", use: "work", value: "email" in data ? data.email : undefined },
        { system: "phone", use: "work", value: data.phone },
        { system: "fax", use: "work", value: data.fax },
      ]),
      address: data.address
        ? [
            {
              line: data.address.line?.filter((line) => line.trim().length > 0),
              city: data.address.city,
              state: data.address.state,
              postalCode: data.address.postalCode,
            },
          ]
        : undefined,
      identifier: pruneSystemValuePairs([
        { system: Provider.Identifier.NPI, value: data.npi },
        { system: Provider.Identifier.LicenseNumber, value: data.licenseNumber },
        { system: Provider.Identifier.DEA, value: data.deaNumber },
        { system: PHOTON_IDENTIFIER_SYSTEM_PRESCRIBERS, value: data.photonPrescriberId },
      ]),
      extension: toFhirExtensions([
        { url: Provider.Extension.ProfilePictureFileId, valueString: data.profilePictureFileId },
        { url: Provider.Extension.LicenseState, valueString: data.licenseState },
        {
          url: Provider.Extension.DosespotRoleSyncRequired,
          valueBoolean: data.dosespotRoleSyncRequired,
        },
        data.dosespotPractitionerRoleType === null
          ? {
              url: Provider.Extension.DosespotPractitionerRoleType,
              valueString: null,
              valueCode: null,
            }
          : {
              url: Provider.Extension.DosespotPractitionerRoleType,
              valueString: data.dosespotPractitionerRoleType,
            },
      ]),
      qualification: data.qualifications
        ? Provider.toFhirQualifications(data.qualifications)
        : undefined,
    };
  }

  /**
   * Converts domain qualification entries to FHIR Practitioner.qualification[] items.
   * Each entry produces a state license qualification and optionally a DEA qualification.
   */
  static toFhirQualifications(
    entries: ProviderQualificationEntry[],
  ): Practitioner["qualification"] {
    const qualifications: NonNullable<Practitioner["qualification"]> = [];

    for (const entry of entries) {
      const issuer = {
        identifier: {
          system: Provider.Qualification.LicenseStateSystem,
          value: entry.state,
        },
      };

      qualifications.push({
        code: {
          coding: [{ system: Provider.Qualification.StateLicense, code: entry.licenseNumber }],
        },
        issuer,
      });

      if (entry.deaNumber) {
        qualifications.push({
          code: { coding: [{ system: Provider.Qualification.DeaLicense, code: entry.deaNumber }] },
          issuer,
        });
      }
    }

    return qualifications;
  }

  /**
   * Updates a provider's FHIR resource.
   *
   * If updating the provider's name, use {@link updateProviderAndSyncCal} instead
   * to keep Cal.com in sync.
   *
   * @param medplum - The Medplum client.
   * @param providerId - The ID of the provider.
   * @param data - The provider data.
   * @returns The provider.
   */
  static async update(
    medplum: MedplumClient,
    providerId: string,
    data: UpdateProviderRequest,
  ): Promise<Provider> {
    const fhirData = Provider.toFhirObject(data);

    // Preserve non-Vibrant qualifications (e.g. board certifications) when updating,
    // since patchResource replaces the entire qualification[] array.
    if (data.qualifications && fhirData.qualification) {
      const existing = await medplum.readResource("Practitioner", providerId);
      const nonVibrant = (existing.qualification ?? []).filter(
        (q) =>
          !q.code?.coding?.some(
            (c) =>
              c.system === Provider.Qualification.StateLicense ||
              c.system === Provider.Qualification.DeaLicense,
          ),
      );
      fhirData.qualification = [...nonVibrant, ...fhirData.qualification];
    }

    const resource = await Provider.patchResource(medplum, { id: providerId, patch: fhirData });
    return new Provider(resource);
  }

  /**
   * Patches a provider with a query.
   *
   * @deprecated use Provider.update instead.
   *
   * @param medplum - The Medplum client.
   * @param providerId - The ID of the provider.
   * @param patchQuery - The patch query.
   * @returns The provider.
   */
  static async patchWithQuery(
    medplum: MedplumClient,
    providerId: string,
    patchQuery: ProviderPatchQuery,
  ): Promise<Provider> {
    let practitioner = await medplum.readResource("Practitioner", providerId);

    const identifierPatchRequests: IdentifierPatchRequest[] = [];

    if (patchQuery.npi) {
      identifierPatchRequests.push({
        resource: practitioner,
        system: Provider.Identifier.NPI,
        value: patchQuery.npi,
      });
    }

    if (patchQuery.calUserId !== undefined) {
      identifierPatchRequests.push({
        resource: practitioner,
        system: CAL_IDENTIFIER_SYSTEM_USERS,
        value: patchQuery.calUserId?.toString() ?? null,
      });
    }

    if (patchQuery.calUsername !== undefined) {
      identifierPatchRequests.push({
        resource: practitioner,
        system: CAL_IDENTIFIER_SYSTEM_USERS_USERNAME,
        value: patchQuery.calUsername,
      });
    }

    if (patchQuery.calManagedEmail !== undefined) {
      identifierPatchRequests.push({
        resource: practitioner,
        system: CAL_IDENTIFIER_SYSTEM_USERS_EMAIL,
        value: patchQuery.calManagedEmail,
      });
    }

    if (patchQuery.calTeamId !== undefined) {
      identifierPatchRequests.push({
        resource: practitioner,
        system: CAL_IDENTIFIER_SYSTEM_TEAMS,
        value: patchQuery.calTeamId?.toString() ?? null,
      });
    }

    if (patchQuery.calTeamMembershipId !== undefined) {
      identifierPatchRequests.push({
        resource: practitioner,
        system: CAL_IDENTIFIER_SYSTEM_TEAM_MEMBERSHIPS,
        value: patchQuery.calTeamMembershipId?.toString() ?? null,
      });
    }

    if (patchQuery.medmoPrescriberId) {
      identifierPatchRequests.push({
        resource: practitioner,
        system: MEDMO_IDENTIFIER_SYSTEM_PRESCRIBERS,
        value: patchQuery.medmoPrescriberId.toString(),
      });
    }

    if (patchQuery.photonPrescriberId) {
      identifierPatchRequests.push({
        resource: practitioner,
        system: PHOTON_IDENTIFIER_SYSTEM_PRESCRIBERS,
        value: patchQuery.photonPrescriberId,
      });
    }

    if (patchQuery.fullscriptPractitionerId) {
      identifierPatchRequests.push({
        resource: practitioner,
        system: FULLSCRIPT_IDENTIFIER_SYSTEM_PRACTITIONERS,
        value: patchQuery.fullscriptPractitionerId,
      });
    }

    // `!== undefined`, not truthy: an explicit `null` clears the identifier.
    if (patchQuery.hintPractitionerId !== undefined) {
      identifierPatchRequests.push({
        resource: practitioner,
        system: Provider.Identifier.HintPractitionerId,
        value: patchQuery.hintPractitionerId,
      });
    }

    const patchOperations = getIdentifierPatchOperations(practitioner, identifierPatchRequests);

    practitioner = await medplum.patchResource("Practitioner", providerId, patchOperations);
    return new Provider(practitioner);
  }

  /**
   * Ensures there is a user in Cal.com for a member.
   *
   * @param medplum - The Medplum client.
   * @param calAdmin - The Cal admin client.
   * @param memberId - The ID of the member.
   * @returns The member and their Cal credentials.
   */
  static async ensureCalendar(
    medplum: MedplumClient,
    calAdmin: CalAdminClient,
    providerId: string,
  ): Promise<{ provider: Provider; calCredentials?: CalCredentials }> {
    const provider = await Provider.getById(medplum, providerId, false);

    let calCredentials = await searchOneCalCredentials(medplum, {
      providerId,
    });

    /**
     * Legacy providers can have malformed Cal user identifiers. Treat them as missing linkage so
     * calendar remediation can repair the profile instead of hard-failing mid-booking.
     */
    function getProviderCalUserIdSafely(): number | null {
      try {
        return provider.calUserId;
      } catch (error) {
        logger.warn("Provider has malformed Cal user ID while ensuring calendar", {
          providerId: provider.id,
          error,
        });
        return null;
      }
    }

    if (calCredentials) {
      const providerCalUserId = getProviderCalUserIdSafely();
      const hasProviderCalUsername = Boolean(provider.calUsername);
      const accessTokenExpiresAt = dayjs(calCredentials.accessTokenExpiresAt);
      const tenMinutesFromNow = dayjs().add(10, "minutes");

      if (accessTokenExpiresAt.isBefore(tenMinutesFromNow) && providerCalUserId != null) {
        // Refresh the token
        const refreshedTokens = await calAdmin.refreshAccessToken({
          refreshToken: calCredentials.refreshToken,
          calUserId: providerCalUserId,
        });

        // Update stored credentials with new tokens
        const refreshedCalCredentials = await patchCalCredentials(medplum, {
          id: calCredentials.id,
          props: {
            accessToken: refreshedTokens.accessToken,
            accessTokenExpiresAt: refreshedTokens.accessTokenExpiresAt,
            refreshToken: refreshedTokens.refreshToken,
          },
        });

        calCredentials = refreshedCalCredentials;
      }

      if (providerCalUserId != null && hasProviderCalUsername) {
        const providerWithTeam = await Provider.ensurePracticeTeamMembership(medplum, provider);
        return { provider: providerWithTeam, calCredentials };
      }

      logger.warn("Provider has Cal credentials but incomplete Cal linkage, repairing profile", {
        providerId: provider.id,
        calCredentialsId: calCredentials.id,
        hasCalUserId: providerCalUserId != null,
        hasCalUsername: hasProviderCalUsername,
      });
    }

    if (!provider.email || !provider.name) {
      logger.warn(
        "could not create provider calendar because provider email and name are required",
      );
      return { provider };
    }

    // Check if user already exists in Cal.com first
    const existingUser = await calAdmin.findManagedUserByEmail(provider.email);

    const calUser = existingUser
      ? await Provider.linkExistingCalUser({ calAdmin, provider, existingUser })
      : await Provider.createOrRecoverCalUser({
          medplum,
          calAdmin,
          provider,
          email: provider.email,
          name: provider.name,
        });
    if (!calUser) {
      return { provider, calCredentials: undefined };
    }

    const updatedProvider = await Provider.patchWithQuery(medplum, provider.id, {
      calUserId: calUser.user.id,
      calUsername: calUser.user.username,
      calManagedEmail: calUser.user.email,
    });

    // search again - a concurrent ensureCalendar pass can create the credentials after our first
    // search, and a second create leaves the provider with two CalCredentials resources
    calCredentials ??= await searchOneCalCredentials(medplum, { providerId: updatedProvider.id });
    calCredentials = calCredentials
      ? await patchCalCredentials(medplum, {
          id: calCredentials.id,
          props: {
            accessToken: calUser.accessToken,
            refreshToken: calUser.refreshToken,
            accessTokenExpiresAt: calUser.accessTokenExpiresAt,
          },
        })
      : await createCalCredentials(medplum, {
          providerId: updatedProvider.id,
          accessToken: calUser.accessToken,
          refreshToken: calUser.refreshToken,
          accessTokenExpiresAt: calUser.accessTokenExpiresAt,
        });

    // pass the provider just patched - a re-read here can return the version without calUserId
    const providerWithTeam = await Provider.ensurePracticeTeamMembership(medplum, updatedProvider);
    return { provider: providerWithTeam, calCredentials };
  }

  /**
   * Links a provider to a Cal.com managed user that already exists: syncs the display name and
   * issues fresh tokens.
   *
   * @returns The managed user with fresh tokens, or null when the token refresh fails.
   */
  private static async linkExistingCalUser({
    calAdmin,
    provider,
    existingUser,
  }: {
    calAdmin: CalAdminClient;
    provider: Provider;
    existingUser: CalManagedUser["user"];
  }): Promise<CalManagedUser | null> {
    logger.info("Cal.com user already exists for provider, refreshing tokens", {
      providerId: provider.id,
      calUserId: existingUser.id,
    });

    try {
      // If the provider's current name differs from the existing Cal.com managed user's name,
      // update the managed user's display name to avoid stale names in invites.
      if (provider.name && existingUser.name !== provider.name) {
        try {
          await calAdmin.updateManagedUser(existingUser.id, { name: provider.name });
          logger.info("Updated Cal.com managed user name to match provider", {
            calUserId: existingUser.id,
            fromName: existingUser.name,
            toName: provider.name,
          });
        } catch (error) {
          logger.warn("Failed to update Cal.com managed user name", {
            calUserId: existingUser.id,
            providerId: provider.id,
            error,
          });
        }
      }

      const tokens = await calAdmin.refreshManagedUserTokens(existingUser.id);
      return {
        user: {
          ...existingUser,
          name: provider.name || existingUser.name,
        },
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        accessTokenExpiresAt: tokens.accessTokenExpiresAt,
      };
    } catch (error) {
      logger.warn("Failed to refresh tokens for existing Cal.com user", {
        providerId: provider.id,
        calUserId: existingUser.id,
        error,
      });
      return null;
    }
  }

  /**
   * Creates the provider's Cal.com managed user.
   *
   * Concurrent ensureCalendar passes for a new provider (the provider-created job, the calendar
   * page, the onboarding status check) all find no user and all try to create one. Cal answers
   * the losers with 409. Treat that as "the user exists now" and link it instead of throwing.
   *
   * @returns The managed user with tokens, or null when the recovered user's token refresh fails.
   */
  private static async createOrRecoverCalUser({
    medplum,
    calAdmin,
    provider,
    email,
    name,
  }: {
    medplum: MedplumClient;
    calAdmin: CalAdminClient;
    provider: Provider;
    email: string;
    name: string;
  }): Promise<CalManagedUser | null> {
    let timeZone: string | undefined;
    try {
      const practice = await provider.getPractice(medplum);
      timeZone = practice?.timezone;
    } catch (_error) {
      // best-effort
    }

    try {
      const calUser = await calAdmin.createManagedUser({
        email,
        name,
        timeZone: timeZone ?? "America/Los_Angeles", // default fallback
        timeFormat: 12,
      });
      logger.info("Created new Cal.com user for provider", {
        providerId: provider.id,
        calUserId: calUser.user.id,
      });
      return calUser;
    } catch (error) {
      if (!isCalConflictError(error)) {
        throw error;
      }

      const recoveredUser = await findManagedUserAfterConflict({ calAdmin, email });
      // 409 but still not findable - nothing to link, surface the original error
      if (!recoveredUser) {
        throw error;
      }

      logger.info("Cal.com user was created concurrently, linking the existing user", {
        providerId: provider.id,
        calUserId: recoveredUser.id,
      });
      return Provider.linkExistingCalUser({ calAdmin, provider, existingUser: recoveredUser });
    }
  }

  /**
   * Joins the provider to their practice Cal.com team when the membership is missing.
   *
   * Runs on every ensureCalendar pass, not only the pass that creates the Cal user. The team add
   * can fail on its own (Cal 5xx, a stale Medplum read), and a later pass finds the Cal user and
   * credentials in place. Without this check it would return before the team step, and the
   * provider would stay outside the team with no bookable event types.
   *
   * Best-effort: logs a failure and returns the provider unchanged.
   *
   * @param medplum - The Medplum client.
   * @param provider - A current copy of the provider, with its Cal user linkage.
   * @returns The provider, with team membership identifiers when the add succeeded.
   */
  private static async ensurePracticeTeamMembership(
    medplum: MedplumClient,
    provider: Provider,
  ): Promise<Provider> {
    // hot path - most calls land here, so skip the practice lookup entirely
    if (provider.calTeamMembershipId != null && provider.calTeamId != null) {
      return provider;
    }

    try {
      const practice = await provider.getPractice(medplum);
      if (!practice?.calTeamId) {
        return provider;
      }

      // All providers get ADMIN role so they can view/edit team event types
      // See: https://cal.com/docs/platform/atoms/event-type#event-type-settings
      const result = await addProviderToTeam({
        medplum,
        provider,
        providerId: provider.id,
        practiceId: practice.id,
        role: "ADMIN",
      });
      // warn on failure - no backoff here, so a provider Cal keeps rejecting retries on every
      // calendar load, and this log line is the only signal that one is stuck
      if (!result.success) {
        logger.warn("Could not add provider to practice Cal.com team", {
          providerId: provider.id,
          practiceId: practice.id,
          error: result.error,
        });
        return provider;
      }
      logger.info("Added provider to practice Cal.com team", {
        providerId: provider.id,
        practiceId: practice.id,
        membershipId: result.membershipId,
      });

      return result.provider ?? provider;
    } catch (error) {
      // Don't fail calendar setup if team membership fails - the next pass retries it
      logger.warn("Error adding provider to Cal.com team (non-critical)", {
        error: error instanceof Error ? error.message : String(error),
        providerId: provider.id,
      });
      return provider;
    }
  }

  static subscribe(
    medplum: MedplumClient,
    query: { id: string },
    callback: SubscriptionCallback<Provider>,
  ) {
    return Provider.subscribeToResource(medplum, { _id: query.id }, (provider, metadata) => {
      callback(new Provider(provider), metadata);
    });
  }

  /**
   * Updates the role of the provider.
   *
   * @param medplum - The Medplum client.
   * @param role - The role to update to.
   */
  async updateRole(medplum: MedplumClient, role: string) {
    let providerRole: ProviderRole | null | undefined = this.providerRole;

    if (!providerRole) {
      providerRole = await ProviderRole.searchOne(medplum, {
        practitioner: this.referenceObject,
      });
    }

    if (!providerRole) {
      throw new Error("Cannot update provider role. PractitionerRole not found");
    }

    // Get the current role before updating
    const currentRole = providerRole.roleId;

    // Update the role
    this.providerRole = await ProviderRole.update(medplum, providerRole.id, { role });

    // Check if onboarding should be reset based on role transitions
    if (currentRole) {
      const shouldResetOnboarding = this.shouldResetOnboardingForRoleTransition(currentRole, role);

      if (shouldResetOnboarding) {
        logger.info("Resetting onboarding status due to role transition", {
          providerId: this.id,
          fromRole: currentRole,
          toRole: role,
        });
        await this.resetOnboardingStatus(medplum);
      }
    }
  }

  /**
   * Determines if the onboarding status should be reset based on role transition.
   *
   * @param fromRole - The current role.
   * @param toRole - The new role.
   * @returns True if onboarding should be reset, false otherwise.
   */
  private shouldResetOnboardingForRoleTransition(fromRole: string, toRole: string): boolean {
    // 1. Upgraded from non-prescriber to prescriber
    if (
      fromRole === VibrantRoles.NonPrescribingProvider &&
      toRole === VibrantRoles.PrescribingProvider
    ) {
      return true;
    }

    // 2. Upgraded from admin to either non-prescriber or prescriber
    if (
      fromRole === VibrantRoles.Admin &&
      (toRole === VibrantRoles.NonPrescribingProvider ||
        toRole === VibrantRoles.PrescribingProvider)
    ) {
      return true;
    }

    // 3. Downgraded from prescriber to non-prescriber
    if (
      fromRole === VibrantRoles.PrescribingProvider &&
      toRole === VibrantRoles.NonPrescribingProvider
    ) {
      return true;
    }

    return false;
  }

  /**
   * Creates a new Provider instance with updated data for client-side state.
   *
   * @param data - The data to update in local state.
   * @returns A new Provider instance with the updated data.
   */
  withOptimisticUpdate(data: {
    name?: string;
    roleId?: string;
    isPracticeAdmin?: boolean;
    dosespotPractitionerRoleType?: DosespotPractitionerRoleType | null;
  }): Provider {
    // Clone the underlying resource with updated name if provided
    let updatedResource = { ...this.resource };
    if (data.name !== undefined) {
      updatedResource.name = toHumanName(data.name);
    }

    if (data.dosespotPractitionerRoleType !== undefined) {
      updatedResource = applyProviderDosespotPractitionerRoleType({
        resource: updatedResource,
        roleType: data.dosespotPractitionerRoleType,
      });
    }

    const newProvider = new Provider(updatedResource);

    // Copy existing relations
    newProvider.providerRole = this.providerRole;
    newProvider.role = this.role;

    // Update providerRole if we have one and isPracticeAdmin changed
    if (newProvider.providerRole && data.isPracticeAdmin !== undefined) {
      const providerRoleResource = newProvider.providerRole.serialize();
      const existingExtensions = providerRoleResource.extension ?? [];
      const filteredExtensions = existingExtensions.filter(
        (ext) => ext.url !== ProviderRole.Extension.IsPracticeAdmin,
      );
      const updatedProviderRoleResource = {
        ...providerRoleResource,
        extension: [
          ...filteredExtensions,
          { url: ProviderRole.Extension.IsPracticeAdmin, valueBoolean: data.isPracticeAdmin },
        ],
      };
      newProvider.providerRole = new ProviderRole(updatedProviderRoleResource);
    }

    // Update role if roleId changed
    if (data.roleId !== undefined && newProvider.providerRole) {
      const providerRoleResource = newProvider.providerRole.serialize();

      // Update the role coding in providerRole while preserving other codings/text.
      const existingCodeableConcepts = providerRoleResource.code ?? [];
      let didUpdateCoding = false;

      const updatedCodeableConcepts = existingCodeableConcepts.map((concept) => {
        const codings = concept.coding ?? [];
        const roleCodingIndex = codings.findIndex(
          (coding) => coding.system === ProviderRole.Code.Role,
        );

        if (roleCodingIndex < 0) {
          return concept;
        }

        didUpdateCoding = true;

        const updatedCodings = [...codings];
        updatedCodings[roleCodingIndex] = {
          ...updatedCodings[roleCodingIndex],
          code: data.roleId,
        };

        return {
          ...concept,
          coding: updatedCodings,
        };
      });

      if (!didUpdateCoding) {
        if (updatedCodeableConcepts.length === 0) {
          updatedCodeableConcepts.push({
            coding: [{ system: ProviderRole.Code.Role, code: data.roleId }],
          });
        } else {
          const firstConcept = updatedCodeableConcepts[0];
          updatedCodeableConcepts[0] = {
            ...firstConcept,
            coding: [
              ...(firstConcept.coding ?? []),
              { system: ProviderRole.Code.Role, code: data.roleId },
            ],
          };
        }
      }

      const updatedProviderRoleResource = {
        ...providerRoleResource,
        code: updatedCodeableConcepts,
      };
      newProvider.providerRole = new ProviderRole(updatedProviderRoleResource);

      // Update the role definition based on new roleId
      if (newProvider.role) {
        // Create updated role with new roleId for display purposes
        newProvider.role = newProvider.role.withRoleId(data.roleId);
      }
    }

    return newProvider;
  }
}

// Cal's user list can lag a fresh create - a stale read 275ms after a write was observed
const CONFLICT_LOOKUP_RETRY_DELAYS_MS = [250, 500, 1000];

/**
 * Looks up the managed user that a Cal 409 says exists. Retries with short delays, because the
 * user that the winning pass just created can be missing from the list endpoint for a moment.
 *
 * @returns The managed user, or null when it is still missing after the last retry.
 */
async function findManagedUserAfterConflict({
  calAdmin,
  email,
}: {
  calAdmin: CalAdminClient;
  email: string;
}): Promise<CalManagedUser["user"] | null> {
  let user = await calAdmin.findManagedUserByEmail(email);
  for (const delayMs of CONFLICT_LOOKUP_RETRY_DELAYS_MS) {
    if (user) {
      return user;
    }
    await sleep(delayMs);
    user = await calAdmin.findManagedUserByEmail(email);
  }
  return user;
}
